import { toast } from "@ui/molecules/Toasts/toast";
import { getVideoAspectRatio } from "@ui/molecules/VideoCarousel/VideoCarousel";
import { useVolumeControl } from "@ui/molecules/VideoPreview/VolumeControl";
import {
	dispatchVideoPreviewPauseAll,
	dispatchVideoPreviewVolumeChanged,
	readStoredVideoMuted,
	readStoredVideoVolume,
	VIDEO_PREVIEW_PAUSE_ALL_EVENT,
} from "@ui/molecules/VideoPreview/videoPreviewVolume";
import {
	type Accessor,
	createEffect,
	createMemo,
	createSignal,
	onCleanup,
	onMount,
	untrack,
} from "solid-js";
import { getBoardClip } from "@/client/sdk.gen";
import type { BoardsByVideoIdsEntryResponse } from "@/client/types.gen";
import { useClipBoardMembership } from "~/modules/shared/boards/boardsContext";
import type { VideoEvidence } from "../../../ChatEventTypes";
import type { EditClipEventDetail } from "../../ChatLayout/ChatLayout";

/** Subset of VideoPanelCarousel props — pass the reactive props object, never `{ ...props }`. */
export type UseCarouselHostProps = {
	activeSlide: number;
	setActiveSlide: (slide: number) => void;
	videoEvidence: VideoEvidence[];
	projectId?: string;
	showAddToBoardButton?: boolean;
	animateSlideChanges?: boolean;
	instantJumpToken?: number;
	/** When false, the active clip does not start playing until the user presses play. */
	autoPlay?: boolean;
};

type UseCarouselOptions = {
	autoPlayNextSlide?: Accessor<boolean>;
};

const dispatchMoveEvent = (
	existingBoard: BoardsByVideoIdsEntryResponse,
	clip: VideoEvidence,
) => {
	const moveEvent: EditClipEventDetail = {
		type: "open_move_board_panel",
		clip: existingBoard.clip,
		video: {
			id: clip.video_id,
		},
		board: existingBoard.board,
	};
	document.dispatchEvent(
		new CustomEvent("open_move_board_panel", {
			detail: moveEvent,
		}),
	);
};

export const useCarousel = (
	hostProps: UseCarouselHostProps,
	options: UseCarouselOptions = {},
) => {
	const shouldAutoPlayNextSlide = () => options.autoPlayNextSlide?.() ?? true;

	const activeVideo = () => hostProps.videoEvidence[hostProps.activeSlide];
	const startsPaused = () => hostProps.autoPlay === false;
	const [userPaused, setUserPaused] = createSignal(startsPaused());
	const [mediaPlaying, setMediaPlaying] = createSignal(false);
	const [overlayContentVisible, setOverlayContentVisible] = createSignal(true);
	const volume = useVolumeControl({
		getVideoElement: () => undefined,
	});

	const [currentVideoProgressPercent, setCurrentVideoProgressPercent] =
		createSignal(0);
	const [seekRequest, setSeekRequest] = createSignal<{
		id: number;
		slideIndex: number;
		clipRelativeSeconds: number;
	}>();
	const [animateSlides, setAnimateSlides] = createSignal(false);
	const [settledInstantJumpToken, setSettledInstantJumpToken] = createSignal(0);
	const [detectedPortrait, setDetectedPortrait] = createSignal<boolean | null>(
		null,
	);

	createEffect(() => {
		const token = hostProps.instantJumpToken ?? 0;
		if (token === settledInstantJumpToken()) {
			return;
		}
		requestAnimationFrame(() => {
			setSettledInstantJumpToken(token);
		});
	});

	createEffect(() => {
		hostProps.activeSlide;
		setDetectedPortrait(null);
		// Preserve the user's playback intent across slide changes. The initial
		// value still respects autoPlay=false, so opening the panel stays paused.
		setMediaPlaying(false);
		setCurrentVideoProgressPercent(0);
		queueMicrotask(() => {
			dispatchVideoPreviewVolumeChanged({
				originator: volume.originatorId,
				volume: readStoredVideoVolume(),
				muted: readStoredVideoMuted(),
			});
		});
	});

	const isPortraitVideo = (videoEvidence: VideoEvidence) =>
		getVideoAspectRatio(videoEvidence) <= 1;

	const hasMixedOrientations = createMemo(() => {
		const hasPortrait = hostProps.videoEvidence.some(isPortraitVideo);
		const hasLandscape = hostProps.videoEvidence.some(
			(videoEvidence) => !isPortraitVideo(videoEvidence),
		);
		return hasPortrait && hasLandscape;
	});

	const isActivePortrait = () => {
		const video = activeVideo();
		if (video?.aspect_ratio != null && video.aspect_ratio > 0) {
			return video.aspect_ratio <= 1;
		}
		const detected = detectedPortrait();
		if (detected !== null) {
			return detected;
		}
		return video ? isPortraitVideo(video) : false;
	};

	const onSlideVideoDimensions = (
		slideIndex: number,
		size: { width: number; height: number },
	) => {
		if (slideIndex !== untrack(() => hostProps.activeSlide)) {
			return;
		}
		if (size.width <= 0 || size.height <= 0) {
			return;
		}
		setDetectedPortrait(size.width <= size.height);
	};

	const nextSlideIndex = (currentSlide: number) => {
		const next = currentSlide + 1;
		if (next < hostProps.videoEvidence.length) {
			return next;
		}
		return 0;
	};

	const onClipPlaybackTime = (
		slideIndex: number,
		videoEvidence: VideoEvidence,
		seconds: number,
	) => {
		const clipDuration = Math.max(
			0,
			videoEvidence.end_time - videoEvidence.start_time,
		);
		if (clipDuration <= 0) {
			setCurrentVideoProgressPercent(0);
			return;
		}
		setCurrentVideoProgressPercent(
			Math.min(100, (seconds / clipDuration) * 100),
		);

		if (userPaused() || !shouldAutoPlayNextSlide()) {
			return;
		}

		const activeSlide = hostProps.activeSlide;
		if (slideIndex !== activeSlide) {
			return;
		}

		const atClipEnd = seconds >= clipDuration - 0.05;
		if (!atClipEnd) {
			return;
		}

		const next = nextSlideIndex(activeSlide);
		if (next !== activeSlide) {
			hostProps.setActiveSlide(next);
		}
	};

	const seekActiveClip = (clipRelativeSeconds: number) => {
		setSeekRequest((previous) => ({
			id: (previous?.id ?? 0) + 1,
			slideIndex: hostProps.activeSlide,
			clipRelativeSeconds,
		}));
	};

	// Scoped to the slide it was made on: a slide becoming active later must
	// not replay a seek meant for another clip.
	const seekRequestForSlide = (slideIndex: number) => {
		const request = seekRequest();
		return request?.slideIndex === slideIndex ? request : undefined;
	};

	const onActiveVideoPlay = (slideIndex: number) => {
		if (slideIndex === hostProps.activeSlide) {
			setMediaPlaying(true);
		}
	};

	const onActiveVideoPause = (slideIndex: number) => {
		if (slideIndex === hostProps.activeSlide) {
			setMediaPlaying(false);
		}
	};

	const pausePanelPlayback = () => {
		setUserPaused(true);
		setMediaPlaying(false);
	};

	onMount(() => {
		if (hostProps.animateSlideChanges === true) {
			requestAnimationFrame(() => {
				setAnimateSlides(true);
			});
		}

		document.addEventListener(
			VIDEO_PREVIEW_PAUSE_ALL_EVENT,
			pausePanelPlayback,
		);
		onCleanup(() => {
			document.removeEventListener(
				VIDEO_PREVIEW_PAUSE_ALL_EVENT,
				pausePanelPlayback,
			);
		});
	});

	const clipBoard = useClipBoardMembership(
		hostProps.projectId,
		hostProps.showAddToBoardButton === false
			? []
			: hostProps.videoEvidence.map((video) => ({
					video_id: video.video_id,
					start: video.start_time,
					end: video.end_time,
					video_duration: video.video_duration,
				})),
		{
			appEmbedded: false,
		},
	);

	const activeExistingBoard = () => clipBoard.getClipBoardEntry(activeVideo());

	const onAddToBoard = hostProps.showAddToBoardButton
		? async () => {
				dispatchVideoPreviewPauseAll();
				const clip = activeVideo();
				const existingBoard = activeExistingBoard();

				if (existingBoard?.board && existingBoard?.clip) {
					const boardId = existingBoard.board?.id;
					const clipId = existingBoard.clip?.id;
					let boardEntry = existingBoard;
					if (boardId && clipId) {
						const res = await getBoardClip({
							path: {
								board_id: boardId,
								clip_id: clipId,
							},
							query: { num_frames: 50 },
						});
						if (res.error) {
							toast.error({
								title: "Failed to load clip",
								description: (res.error as { message?: string }).message,
							});
							return;
						}
						boardEntry = {
							board: existingBoard.board,
							clip: res.data,
						};
					}
					dispatchMoveEvent(boardEntry, clip);

					return;
				}

				await clipBoard.addClip(clip);
			}
		: undefined;

	return {
		activeVideo,
		userPaused,
		setUserPaused,
		mediaPlaying,
		onActiveVideoPlay,
		onActiveVideoPause,
		overlayContentVisible,
		setOverlayContentVisible,
		volume,
		currentVideoProgressPercent,
		onClipPlaybackTime,
		seekActiveClip,
		seekRequestForSlide,
		isPortraitVideo,
		isActivePortrait,
		hasMixedOrientations,
		onSlideVideoDimensions,
		onAddToBoard,
		animateSlides,
		shouldAnimateSlides: () =>
			hostProps.animateSlideChanges === true &&
			animateSlides() &&
			(hostProps.instantJumpToken ?? 0) === settledInstantJumpToken(),
		clipInBoard: () => {
			const video = activeVideo();
			return video ? clipBoard.isClipOnBoard(video) : false;
		},
		clipMembershipLoading: clipBoard.isLoading,
	};
};
